import { Box, Typography, Link, Stack, CircularProgress, useTheme, Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { Link as RouterLink, useNavigate } from "react-router";

import FullTaskLayout from "../../../Layouts/FullTask";
import OrderRecapCard from "../../../Components/Cards/OrderRecapCard/order-recap-config";
import { routePaths } from "../../../Router/router";
import ConfigOrderOptionsForm from "../../../Components/Config/ConfigOrderOptionsForm/config-order-options-form";
import FullTaskTopbar from "../../../Components/Topbars/FullTaskTopbar";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { basketService, cacheService, orderService } from "../../../Core/Api/services";
import { BasketStatus } from "../../../Core/Api/Types/basket-status";
import { useDispatch, useSelector } from "react-redux";
import { GetBasket, ResetBasket, StoreBasket } from "../../../Redux/Reducers/redux-slice-basket";
import { BasketGroupedItems, BasketTopLevelItem } from "../basket";
import { GroupByToMap, UserHasAcl } from "../../../Utils/helpers";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { DbCustomerInfos } from "../../../Core/Api/Types/db-customer-infos";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { OrderCheckoutRequest } from "../../../Core/Api/Types/order-checkout-request";
import { DbOrderBenefitTypeInfos } from "../../../Core/Api/Types/db-order-benefit-type-infos";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { useTranslation } from "react-i18next";

function BasketCheckout() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const enqueueSnackbar = useCustomSnackbar();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customers, setCustomers] = useState<DbCustomerInfos[]>([]);
  const [costCenters, setCostCenters] = useState<DbOrderBenefitTypeInfos[]>([]);
  const [checkout, setCheckout] = useState<OrderCheckoutRequest>({
    isImportant: false,
    clearBasket: false,
    clearBasketOptions: false,
    autoPlay: UserHasAcl(AclTypes.aclOrderPlayWithoutApproval),
    followUpEmail: cacheService.GetPlatformMode() === PlatformMode.Browser,
    restoreExpedit: false,
    hide: false,
    comment: "",
    externalReference: "",
    dueDate: null,
    costBenefitsLinkId: null,
    externalContractDealIds: null,
    priceOverrides: null,
    contacts: null,
  });
  const basket = useSelector(GetBasket);
  const customerExternalIdLabel = cacheService.GetCustomerExternalIdLabel();

  // Make grouped items tree when redux basket changes
  const basketGrouped: BasketTopLevelItem[] = useMemo(() => {
    if (!basket) return [];

    let grouped = GroupByToMap(basket.items!, (x) => x.titleId);
    let output: BasketTopLevelItem[] = [];

    grouped.forEach((value: DbOrderBasketItemInfos[], key: number | null) => {
      let titleGroup: BasketTopLevelItem = {
        id: key!,
        name: value[0].titleInfos?.name!,
        catalogType: GetMetadataByTagName(value[0].titleInfos, "titleType")?.value || "N/A",
        productionYear: GetMetadataByTagName(value[0].titleInfos, "productionDate")?.value || "N/A",
        customerTitleId:
          value[0].titleInfos?.titleCustomerId != null
            ? customerExternalIdLabel + "#" + value[0].titleInfos?.titleCustomerId
            : "",
        items: [],
        layer: value[0].titleInfos?.layer!,
      };

      let groupByLayer = GroupByToMap(value, (x) => x.objectLayer);

      groupByLayer.forEach((items: DbOrderBasketItemInfos[], key: ObjectLayer | null) => {
        let itemsByLayer: BasketGroupedItems = {
          layer: key!,
          items: items,
        };

        titleGroup.items.push(itemsByLayer);
      });

      output.push(titleGroup);
    });

    console.log("memo:basketGrouped reload");
    return output;
  }, [basket]);

  const priceTotal = useMemo(() => {
    if (!basket || !basket.items) return 0;

    let sum = 0;

    basket.items?.forEach((x) => {
      if (x.overridenPrice?.price) sum += x.overridenPrice?.price;
      else if (x.totalPrice?.price) sum += x.totalPrice?.price;
    });

    return sum;
  }, [basket]);

  const CheckContacts = useCallback(() => {
    let ackBillingEnabled = cacheService.GetAuthLoginResponse()?.ackBillingEnabled;
    if (!ackBillingEnabled) return true;

    if (basket.items)
      for (let i = 0; i < basket.items?.length; ++i) {
        if (
          basket.items[i].overridenPrice !== undefined &&
          basket.items[i].overridenPrice !== null &&
          basket.items[i].overridenPrice?.price !== undefined &&
          basket.items[i].overridenPrice?.price !== null &&
          basket.items[i].overridenPrice?.price! > 0 &&
          (basket.items[i].billingEmails === undefined ||
            basket.items[i].billingEmails === null ||
            basket.items[i].billingEmails?.length === 0)
        )
          return false;
      }

    return true;
  }, [basket?.items]);

  const proceed = useCallback(() => {
    if (!CheckContacts()) {
      enqueueSnackbar(t("alert:warning.missingContactEmails"), "warning");
      return;
    }

    setIsSubmitting(true);

    // Transform response before post to backend
    let checkoutRequest = Object.assign({}, checkout);

    basket.items?.forEach((item) => {
      // Price override
      if (item.overridenPrice) {
        if (!checkoutRequest.priceOverrides) checkoutRequest.priceOverrides = [];

        checkoutRequest.priceOverrides.push({ itemId: item.id, price: item.overridenPrice });
      }

      // Contacts
      if (item.billingEmails) {
        if (!checkoutRequest.contacts) checkoutRequest.contacts = [];

        checkoutRequest.contacts.push({ itemId: item.id, emails: item.billingEmails });
      }

      // Contracts
      if (item.customerIdOverride || item.contractExternalDealId || item.contractExternalCustomerId) {
        if (!checkoutRequest.externalContractDealIds) checkoutRequest.externalContractDealIds = [];

        checkoutRequest.externalContractDealIds.push({
          itemId: item.id,
          externalContractDealId: item.contractExternalDealId,
          externalContractCustomerId:
            item.contractExternalCustomerId ??
            item.contractDeals?.find((x) => x.externalId === item.contractExternalDealId)?.customerId ??
            null,
          customerIdOverride: item.customerIdOverride,
        });
      }
    });

    basketService
      .Checkout(checkoutRequest)
      .then((response) => {
        if (response.success) {
          if (checkoutRequest.clearBasket) dispatch(ResetBasket());

          navigate(routePaths.basketSuccess.path + "/" + response.orderId);
          console.info("basket: checkout ok with order Id = " + response.objectId);
        }
      })
      .catch((error) => {
        console.error("basket: checkout error =>" + error);
        setIsSubmitting(false);
      });
  }, [checkout, basket?.items]);

  /*
  Main loader
  */
  useEffect(() => {
    // Load basket data
    basketService.GetBasketByStep(BasketStatus.Ready).then((response) => {
      if (response.basket) {
        if (response.basket.status !== BasketStatus.Ready) {
          navigate(routePaths.basket.path);
          return;
        }

        dispatch(StoreBasket(response.basket));
        setIsLoading(false);
      }
    });

    if (UserHasAcl(AclTypes.aclOrderOverrideItemCustomer))
      orderService.GetCustomersList().then((response) => {
        if (response.customers) setCustomers(response.customers);
      });

    if (UserHasAcl(AclTypes.aclOrderAssignBenefitCostLinkCreate))
      orderService.GetBenefitsTypes().then((response) => {
        if (response.types) setCostCenters(response.types);
      });
  }, []);

  return (
    <FullTaskLayout
      withContainer
      sxContainer={{
        background: theme.palette.background.default,
      }}
      TopBar={
        <FullTaskTopbar
          title={t("page:fullTaskTopbar.title.orderCheckout")}
          backRoute={routePaths.home.path}
          rightContent={
            <Stack direction="row" spacing={1}>
              <Stack alignItems="flex-end" textAlign="right" mr={1}>
                <Typography fontWeight={500} variant="subtitle2" color="text.primary">
                  {priceTotal} €
                </Typography>
                <Typography fontWeight={500} color="text.secondary">
                  {t("page:body.titlesWithCountUpperCase_one", { count: basketGrouped.length })}
                  {", "}
                  {t("page:body.itemWithCountUpperCase", { count: basket?.items?.length || 0 })}
                </Typography>
              </Stack>
              <Button
                color="primary"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => proceed()}
                disabled={isLoading}
                loading={isSubmitting}
              >
                {t("button:proceedOrder")}
              </Button>
              {/*<MoreMenu />*/}
            </Stack>
          }
        />
      }
    >
      {isLoading ? (
        <CircularProgress
          sx={{
            position: "absolute",
            top: "50%",
            bottom: "50%",
            left: "50%",
          }}
        />
      ) : (
        <>
          <Box mt={3} mb={4}>
            <Link
              component={RouterLink}
              to={routePaths.basket.path}
              underline="hover"
              variant="body1"
              color="text.secondary"
            >
              <ArrowBackRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
              {t("button:backToConfig")}
            </Link>
          </Box>
          <Typography variant="h3" component="h1" mb={3}>
            {t("page:body.yourOrder")}
          </Typography>

          <Stack spacing={1.5} mb={4}>
            {basketGrouped.map((group) => (
              <OrderRecapCard key={group.id} group={group} customers={customers} />
            ))}
          </Stack>

          <ConfigOrderOptionsForm
            sx={{ mb: 4 }}
            checkout={checkout}
            setCheckout={setCheckout}
            costCenters={costCenters}
          />
        </>
      )}
    </FullTaskLayout>
  );
}

export default BasketCheckout;
